import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type VoiceState = 'idle' | 'loading' | 'ready' | 'recording' | 'transcribing' | 'processing' | 'error';

interface VoiceSliceState {
  state: VoiceState;
  transcriptionText: string;
  error: string | null;
  isModelLoaded: boolean;
  statusMessage: string;
  isSupported: boolean;
}

const initialState: VoiceSliceState = {
  state: 'idle',
  transcriptionText: '',
  error: null,
  isModelLoaded: false,
  statusMessage: '',
  isSupported: typeof navigator !== 'undefined' && 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
};

const voiceSlice = createSlice({
  name: 'voice',
  initialState,
  reducers: {
    setVoiceState: (state, action: PayloadAction<VoiceState>) => {
      state.state = action.payload;
      
      // Clear error when state changes to non-error state
      if (action.payload !== 'error') {
        state.error = null;
      }
    },
    
    setTranscriptionText: (state, action: PayloadAction<string>) => {
      state.transcriptionText = action.payload;
    },
    
    appendTranscriptionText: (state, action: PayloadAction<string>) => {
      state.transcriptionText += action.payload;
    },
    
    setVoiceError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.state = 'error';
      }
    },
    
    setModelLoaded: (state, action: PayloadAction<boolean>) => {
      state.isModelLoaded = action.payload;
    },
    
    setStatusMessage: (state, action: PayloadAction<string>) => {
      state.statusMessage = action.payload;
    },
    
    setVoiceSupported: (state, action: PayloadAction<boolean>) => {
      state.isSupported = action.payload;
    },
    
    resetVoice: (state) => {
      state.state = 'idle';
      state.transcriptionText = '';
      state.error = null;
      state.statusMessage = '';
      // Keep isModelLoaded and isSupported as they are persistent
    },
    
    clearTranscription: (state) => {
      state.transcriptionText = '';
    },
  },
});

export const {
  setVoiceState,
  setTranscriptionText,
  appendTranscriptionText,
  setVoiceError,
  setModelLoaded,
  setStatusMessage,
  setVoiceSupported,
  resetVoice,
  clearTranscription,
} = voiceSlice.actions;

export default voiceSlice.reducer;

// Selectors
export const selectVoiceState = (state: { voice: VoiceSliceState }) => state.voice.state;
export const selectTranscriptionText = (state: { voice: VoiceSliceState }) => state.voice.transcriptionText;
export const selectVoiceError = (state: { voice: VoiceSliceState }) => state.voice.error;
export const selectIsModelLoaded = (state: { voice: VoiceSliceState }) => state.voice.isModelLoaded;
export const selectStatusMessage = (state: { voice: VoiceSliceState }) => state.voice.statusMessage;
export const selectIsVoiceSupported = (state: { voice: VoiceSliceState }) => state.voice.isSupported; 